import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import PlusIcon from '../../../assets/icons/plus.svg?react';
import RefreshIcon from '../../../assets/icons/refresh.svg?react';
import SmsContactItem from './SmsContactItem';
import Pagination from '../../../components/Pagination/Pagination';
import { SkeletonTable } from '../../../components/Skeleton';
import SmsContactAppendSideModal from './SmsContactAppendSideModal';
import SmsContactDeleteModal from './SmsContactDeleteModal';
import { useContactList } from '../../../hooks/queries/smsQueries';
const SmsContacts = () => {
  const [deleteModalOn, setDeleteModalOn] = useState(false);
  const [contactModalOn, setContactModalOn] = useState(false);
  const [target, setTarget] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [refreshMsg, setRefreshMsg] = useState('');
  const [keywordInput, setKeywordInput] = useState('');
  
  const currentPage = parseInt(searchParams.get('page')) || 1;
  const pageSize = parseInt(searchParams.get('pageSize')) || 10;
  const sortBy = searchParams.get('sortBy') || 'CreatedDate';
  const sortOrder = searchParams.get('sortOrder') || 'desc';
  const keyword = searchParams.get('Keyword') || '';
  
  // keywordInput을 URL의 keyword와 동기화
  useEffect(() => {
    if (keyword) {
      setKeywordInput(keyword);
    } else {
      setKeywordInput('');
    }
  }, [keyword]);
  
  // Query Params Memoization
  const queryParams = useMemo(() => {
    const params = {
      Page: currentPage,
      PageSize: pageSize,
      OrderBy: sortBy,
      OrderDirection: sortOrder
    };
    
    if (keyword) {
      params.Keyword = keyword;
    }
    
    return params;
  }, [currentPage, pageSize, sortBy, sortOrder, keyword]);
  
  const { data: contacts, isLoading, error, refetch } = useContactList(queryParams);
  
  
  // 키워드 입력 핸들러
  const handleKeywordChange = (e) => {
    setKeywordInput(e.target.value);
  };
  
  // 키워드 검색 핸들러 (Enter 키)
  const handleKeywordKeyDown = (e) => {
    if (e.key === 'Enter') {
      const value = e.target.value.trim();
      if (value.length >= 2) {
        setSearchParams(prev => {
          const newParams = new URLSearchParams(prev);
          newParams.set('Keyword', value);
          newParams.set('page', '1');
          return newParams;
        });
      } else if (value.length === 0) {
        setSearchParams(prev => {
          const newParams = new URLSearchParams(prev);
          newParams.delete('Keyword');
          newParams.set('page', '1');
          return newParams;
        });
      }
    }
  };

  const handlePageChange = (page) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('page', page.toString());
      return newParams;
    });
  };

  const totalPages = contacts?.Total ? Math.ceil(contacts.Total / pageSize) : 1;

  const handleDeleteSuccess = () => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('page', '1');
      return newParams;
    });
    setDeleteModalOn(false);
    refetch();
  };

  const handleDelete = (contact) => {
    setTarget(contact);
    setDeleteModalOn(true);
  };

  const handleAddNew = () => {
    setContactModalOn(true);
  };

  const handleModalSuccess = () => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('page', '1');
      return newParams;
    });
    setContactModalOn(false);
    refetch();
  };

  const handleRefresh = async () => {
    try {
      setRefreshMsg('...');
      await refetch();
      setRefreshMsg('연락처 목록 새로고침 완료!');
      setTimeout(() => {
        setRefreshMsg('');
      }, 1500);
    } catch (error) {
      console.error('연락처 목록 새로고침 실패:', error);
      setRefreshMsg('연락처 목록 새로고침 실패!');
      setTimeout(() => {
        setRefreshMsg('');
      }, 1500);
    }
  };

  return (
    <>
      <div className="flex justify-between align-center mb-10">
        <div className="flex w-half">
          <input 
            type="text" 
            placeholder="키워드 검색 (2자 이상) - 이름, 연락처" 
            className="input-primary flex-1 text-b2-400" 
            value={keywordInput}
            onChange={handleKeywordChange}
            onKeyDown={handleKeywordKeyDown}
          />
        </div>
        <div className="flex justify-end align-center gap-5">
          <p className="text-b2-400 text-primary">{refreshMsg}</p>
          <button className="btn big-btn btn-style-5 flex align-center gap-3" onClick={handleRefresh}>
            <RefreshIcon color='var(--gray-600)'/>
            <p className="text-b2-400 text-gray-600">새로고침</p>
          </button>
          <button className="btn big-btn btn-primary flex align-center gap-3" onClick={handleAddNew}>
            <PlusIcon color='var(--white)'/>
            <p className="text-b2-400 text-white">연락처 추가</p>
          </button>
        </div>
      </div>
      {isLoading ? (
        <SkeletonTable rows={5} cols={7} />
      ) : error ? (
        <div className="error-message">데이터를 불러오는데 실패했습니다</div>
      ) : (
        <div className="primary-table">
          <table>
            <thead>
              <tr>
                <th>이름</th>
                <th>연락처</th>
                <th>조직</th>
                <th width="50px"></th>
              </tr>
            </thead>
            <tbody>
              {contacts?.Items?.length === 0 ? (
                <tr>
                  <td colSpan="5" className="no-data">
                    <p className="text-b3-700 text-gray-500">연락처 데이터가 없습니다.</p>
                  </td>
                </tr>
              ) : (
                contacts?.Items?.map(contact => (
                  <SmsContactItem 
                    key={contact.ContactSysId} 
                    contact={contact} 
                    onDelete={handleDelete}
                  />
                ))
              )}
            </tbody>
          </table>
          <div className="pagination-wrapper">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      )}
      {deleteModalOn && (
        <SmsContactDeleteModal
          onClose={() => setDeleteModalOn(false)}
          onDeleteSuccess={handleDeleteSuccess}
          contact={target}
        />
      )}
      {contactModalOn && (
        <SmsContactAppendSideModal
          onClose={() => setContactModalOn(false)}
          onSuccess={handleModalSuccess}
        />
      )}
    </>
  );
};

export default SmsContacts;
